import { Fragment, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { cx } from "shared/utils";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { crmAnalyticsApi, DaysOnMarketUnit } from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { PropertyGroupHeader } from "./PropertyGroupHeader";
import type { UnitFilterParams } from "./UnitsFilter";
import { DEFAULT_PER_PAGE, formatDays } from "../constants";
import { groupByProperty, propertyKeyedSelector } from "../utils";

const ROW_GRID_CLASSNAME = "grid grid-cols-[1fr_60px] items-center";

const DaysOnMarketRow = ({ item }: { item: DaysOnMarketUnit }) => (
  <div className={cx(ROW_GRID_CLASSNAME, "px-5 py-3 bg-content-1000-inverted")}>
    <p className="text-sm font-medium text-content-700 truncate">
      {item.unit_title}
    </p>
    <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM} className="w-fit">
      <span className="!font-bold !text-content-1000">
        {item.days_on_market ? formatDays(item.days_on_market) : "-"}
      </span>
    </Badge>
  </div>
);

export const DaysOnMarketModal = ({
  opened,
  onClose,
  filterParams
}: {
  opened: boolean;
  onClose: () => void;
  filterParams?: UnitFilterParams;
}) => {
  const [page, setPage] = useState(1);
  const dict = useUnitsPropertiesDictionary();

  useEffect(() => {
    if (opened) setPage(1);
  }, [opened]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["CRM_ANALYTICS_DAYS_ON_MARKET", page, filterParams],
    queryFn: () =>
      crmAnalyticsApi.getDaysOnMarket({
        ...filterParams,
        page,
        per_page: Number(DEFAULT_PER_PAGE)
      }),
    enabled: opened,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const totalPages = data?.page_count ?? 0;
  const columns = useMemo(
    () => [{ label: dict.Unit }, { label: "Days" }],
    [dict]
  );
  const pagination = useMemo(
    () => ({ total: totalPages, current: page, onChange: setPage }),
    [totalPages, page]
  );
  const groups = useMemo(
    () => groupByProperty(items, propertyKeyedSelector),
    [items]
  );

  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title="Days on market"
      gridClassName={ROW_GRID_CLASSNAME}
      columns={columns}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={items.length === 0}
      emptyText="No records found"
      pagination={pagination}
      panelClassName="max-w-[707px]"
    >
      {groups.map((group) => (
        <Fragment key={group.key}>
          <PropertyGroupHeader name={group.name} />
          {group.items.map((item) => (
            <DaysOnMarketRow key={`${item.id}`} item={item} />
          ))}
        </Fragment>
      ))}
    </AnalyticsDetailModal>
  );
};
